import { Form, Spin, message } from 'antd';
import { Content } from 'antd/es/layout/layout';
import { useContext, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { SDetailLayout } from 'app/components/StaycationUI';
import { AssetDetailTitle } from 'app/components/commons/AssetDetailTitle/AssetDetailTitle';
import {
  PUBLISH_ERROR_TYPE,
  PublishModalError,
} from 'app/components/commons/PublishModalError/PublishModalError';
import { NewPackModal } from 'app/components/pages/Packs/NewPackModal/NewPackModal';
import {
  ViewEditModeContext,
  ViewEditModeContextProvider,
} from 'app/context/ViewEditModeContext/ViewEditModeContext';
import { useSortHotelEntities } from 'app/hooks/data/core/admin/hotels/useSortHotelEntities';
import { useUpdateDayRoomAccess } from 'app/hooks/data/core/admin/hotels/useUpdateDayRoomAccess';
import {
  DAY_ROOM_ACCESS_OPEN_PARAM,
  toDayRoomAccess,
} from 'app/utils/dayRoomAccess';
import type { DayRoomAccessFormValue } from 'app/utils/dayRoomAccess';

import styles from './PackagesRoomsAmenities.module.scss';
import { ActivitiesTable } from './_components/ActivitiesTable';
import { PackagesTable } from './_components/PackagesTable';
import { RoomsTable } from './_components/RoomsTable';
import { groupPackagesByPeriod } from './_components/_utils/packageUtils';
import {
  withAlwaysIncludedActivityPriority,
  withRoomPriority,
} from './_components/_utils/priorityUtils';
import { DayRoomAccessButton } from './_components/dayRoomAccess/DayRoomAccessButton';
import { DayRoomAccessModal } from './_components/dayRoomAccess/DayRoomAccessModal';
import { useActivityActions } from './_hooks/useActivityActions';
import { useEntityNavigation } from './_hooks/useEntityNavigation';
import { useEntityReorder } from './_hooks/useEntityReorder';
import { useHotelEntitiesData } from './_hooks/useHotelEntitiesData';
import { usePackageActions } from './_hooks/usePackageActions';
import { usePublishError } from './_hooks/usePublishError';
import { useRoomActions } from './_hooks/useRoomActions';
import type { PackagesRoomsAmenitiesFormValues } from './_types';

const getScrollableAncestor = (element: HTMLElement) => {
  let parent = element.parentElement;

  while (parent) {
    const { overflowY } = window.getComputedStyle(parent);

    if (
      (overflowY === 'auto' || overflowY === 'scroll') &&
      parent.scrollHeight > parent.clientHeight
    ) {
      return parent;
    }

    parent = parent.parentElement;
  }

  return null;
};

const scrollToSection = (section: HTMLElement | null) => {
  if (!section) {
    return;
  }

  const scroller = getScrollableAncestor(section);

  if (!scroller) {
    section.scrollIntoView({ block: 'start' });

    return;
  }

  scroller.scrollTop +=
    section.getBoundingClientRect().top - scroller.getBoundingClientRect().top;
};

type Props = {
  headerMenu: React.ReactNode;
  headerExtra?: React.ReactNode[];
  hotelId: number;
};

const PackagesRoomsAmenitiesContent = ({
  headerMenu,
  headerExtra,
  hotelId,
}: Props) => {
  const [form] = Form.useForm<PackagesRoomsAmenitiesFormValues>();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isDayRoomAccessModalOpen, setIsDayRoomAccessModalOpen] =
    useState(false);
  const [shouldOpenDayRoomAccess, setShouldOpenDayRoomAccess] = useState(false);
  const roomsSectionRef = useRef<HTMLDivElement>(null);
  const { mode, enterEditMode, enterViewMode, isEditMode } =
    useContext(ViewEditModeContext);
  const history = useHistory();
  const location = useLocation();

  const { mutateAsync: sortHotelEntities } = useSortHotelEntities({ hotelId });
  const { mutateAsync: updateDayRoomAccess } = useUpdateDayRoomAccess(hotelId);

  useEffect(() => {
    const query = new URLSearchParams(location.search);

    if (query.get(DAY_ROOM_ACCESS_OPEN_PARAM) !== 'true') {
      return;
    }

    setShouldOpenDayRoomAccess(true);
    query.delete(DAY_ROOM_ACCESS_OPEN_PARAM);
    history.replace({ search: query.toString() });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Deep link is only read on mount
  }, []);

  // --- Data ---
  const {
    hotel,
    isFetchHotelLoading,
    packages: fetchedPackages,
    rooms: fetchedRooms,
    activities: fetchedActivities,
    dayRoomAccess: fetchedDayRoomAccess,
    refetchPackages,
    refetchRooms,
    refetchActivities,
    isFetchEntitiesLoading,
  } = useHotelEntitiesData(hotelId);

  useEffect(() => {
    if (
      !shouldOpenDayRoomAccess ||
      isFetchHotelLoading ||
      isFetchEntitiesLoading
    ) {
      return;
    }

    scrollToSection(roomsSectionRef.current);
    setIsDayRoomAccessModalOpen(true);
    setShouldOpenDayRoomAccess(false);
  }, [shouldOpenDayRoomAccess, isFetchHotelLoading, isFetchEntitiesLoading]);

  const packages =
    Form.useWatch<typeof fetchedPackages>('packages', form) ?? fetchedPackages;
  const rooms =
    Form.useWatch<typeof fetchedRooms>('rooms', form) ?? fetchedRooms;
  const activities =
    Form.useWatch<typeof fetchedActivities>('activities', form) ??
    fetchedActivities;

  useEffect(() => {
    form.setFieldsValue({
      packages: fetchedPackages,
      rooms: withRoomPriority(fetchedRooms),
      activities: withAlwaysIncludedActivityPriority(fetchedActivities),
    });
  }, [fetchedPackages, fetchedRooms, fetchedActivities, form]);

  // --- Publish error ---
  const {
    publishErrorType,
    missingFieldsError,
    openPublishErrorModal,
    closePublishErrorModal,
  } = usePublishError();

  // --- Actions ---
  const { publishPackage, setupPackage, unpublishPackage, hidePackage } =
    usePackageActions({
      refetchPackages,
      openPublishErrorModal,
      isLegacy: hotel?.isLegacy ?? true,
    });

  const { publishRoom, unpublishRoom } = useRoomActions({
    hotelId,
    refetchRooms,
    openPublishErrorModal,
    isLegacy: hotel?.isLegacy ?? true,
  });

  const { publishActivity, unpublishActivity } = useActivityActions({
    hotelId,
    refetchActivities,
    openPublishErrorModal,
    isLegacy: hotel?.isLegacy ?? true,
  });

  // --- Navigation ---
  const { goToCreateRoom, goToCreateActivity } = useEntityNavigation({
    hotelId,
    isLegacy: hotel?.isLegacy ?? false,
  });

  // --- Reorder ---
  const { handleReorderPackages, handleReorderRooms, handleReorderActivities } =
    useEntityReorder({ form, packages, activities });

  // --- Save ---
  const handleSave = async () => {
    try {
      const values = await form.validateFields();

      await sortHotelEntities({
        rooms: values.rooms.map(({ id, hotelPriority }) => ({
          id,
          hotelPriority: hotelPriority ?? 0,
        })),
        alwaysIncludedExperiences: values.activities
          .filter((activity) => activity.isAlwaysIncludedWithNight)
          .map(({ id, hotelPriority }) => ({
            id,
            hotelPriority: hotelPriority ?? 0,
          })),
      });

      message.success('Hotel successfully saved!');
      enterViewMode();
    } catch {
      message.error('Hotel could not be saved');
    }
  };

  const handleSaveDayRoomAccess = async (value: DayRoomAccessFormValue) => {
    await updateDayRoomAccess(toDayRoomAccess(value));
    message.success('Day access successfully saved!');
  };

  // --- Derived state ---
  const { daytimePackages, nightStayPackages } =
    groupPackagesByPeriod(packages);

  if (isFetchHotelLoading) {
    return null;
  }

  const isLegacy = hotel?.isLegacy ?? true;

  return (
    <>
      <Form form={form} initialValues={{ packages, rooms, activities }}>
        <SDetailLayout
          title={
            <AssetDetailTitle
              assetTitle={hotel?.name ?? `Hôtel #${hotelId}`}
              hotelName={hotel?.name}
              isNewDataModel={!hotel?.isLegacy}
            />
          }
          backRoute="/hotels"
          mode={mode}
          onEdit={enterEditMode}
          onCancel={enterViewMode}
          onSave={handleSave}
          headerMenu={headerMenu}
          headerExtra={headerExtra}
          isDirty={true}
        >
          <Spin
            spinning={isFetchEntitiesLoading}
            wrapperClassName={styles.mainContent}
          >
            <Content className={`hotel-detail__content ${styles.content}`}>
              <Form.Item name="packages" hidden>
                <input />
              </Form.Item>
              <Form.Item name="rooms" hidden>
                <input />
              </Form.Item>
              <Form.Item name="activities" hidden>
                <input />
              </Form.Item>

              <PackagesTable
                daytimePackages={daytimePackages}
                nightStayPackages={nightStayPackages}
                isLegacy={isLegacy}
                onCreatePackage={() => setIsCreateModalOpen(true)}
                onPublish={publishPackage}
                onSetup={setupPackage}
                onUnpublish={unpublishPackage}
                onHide={hidePackage}
                onReorder={isEditMode ? handleReorderPackages : undefined}
                activities={activities}
                rooms={rooms}
              />

              <ActivitiesTable
                hotelId={hotelId}
                activities={activities}
                onCreateActivity={goToCreateActivity}
                onPublish={publishActivity}
                onUnpublish={unpublishActivity}
                onReorder={isEditMode ? handleReorderActivities : undefined}
                isLegacy={isLegacy}
              />

              <RoomsTable
                containerRef={roomsSectionRef}
                rooms={rooms}
                isLegacy={isLegacy}
                onCreateRoom={goToCreateRoom}
                onPublish={publishRoom}
                onUnpublish={unpublishRoom}
                onReorder={isEditMode ? handleReorderRooms : undefined}
                headerExtra={
                  !isLegacy && (
                    <DayRoomAccessButton
                      value={fetchedDayRoomAccess}
                      disabled={isEditMode}
                      onClick={() => setIsDayRoomAccessModalOpen(true)}
                    />
                  )
                }
              />
            </Content>
          </Spin>
        </SDetailLayout>
      </Form>

      <NewPackModal
        open={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        hotelId={hotelId}
      />

      {!isLegacy && (
        <DayRoomAccessModal
          open={isDayRoomAccessModalOpen}
          value={fetchedDayRoomAccess}
          onSave={handleSaveDayRoomAccess}
          onClose={() => setIsDayRoomAccessModalOpen(false)}
        />
      )}

      <PublishModalError
        type={publishErrorType ?? PUBLISH_ERROR_TYPE.PACKAGE}
        missingFields={missingFieldsError ?? []}
        isOpen={publishErrorType !== null}
        handleOk={closePublishErrorModal}
      />
    </>
  );
};

export const PackagesRoomsAmenities = (props: Props) => {
  return (
    <ViewEditModeContextProvider>
      <PackagesRoomsAmenitiesContent {...props} />
    </ViewEditModeContextProvider>
  );
};
